import { COUPON_STATUS } from "@/constants/coupon";
import { isBigCompanyOrder, VIP_RIGHT_IDENTIFICATION } from "@/constants/vip";
import {
  getBizOrderList,
  getCounponNum,
  getCurrentVip,
  getGdCouponNumber,
  getGdUserDownloadRight,
  getGdUserOrgs,
  getGdVipInfo,
  getGdVipUnpaid,
  getGrocery,
  getMeOrg,
  getUserRightCount,
} from "@/services";
import loginTracker from "@/tracker/report/loginTracker";
import { isProd } from "@/utils/env";
import { getOrgIdByUser, isGdMerged } from "@/utils/gaoding";
import { filterMaterailVip } from "@/utils/gaodingVip";
import getScrollbarWidth from "@/utils/getScrollbarWidth";
import onlyVisibleRun from "@/utils/onlyVisibleRun";
import { env } from "@/utils/request";
import { storeSetHookMiddire } from "@/utils/storeSetHook";
import useRequest from "@/utils/useReq";
import { useMemoizedFn, useMount, useUpdateEffect } from "ahooks";
import Cookies from "js-cookie";
import { once, pick } from "lodash";
import { useEffect } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import app from "./modules/app";
import board from "./modules/board";
import dialog from "./modules/dialog";
import filter from "./modules/filter";
import style from "./modules/style";
import user from "./modules/user";
import vip from "./modules/vip";
import { useTabShare } from "./share";
import { useMasonryCompute } from "./useWidthCompute";

/**
 *  @template T
 *  @typedef {()=>T} creator
 */

// 聚合所有mod
/**
 * @template T1
 * @template T2
 * @template T3
 * @template T4
 * @template T5
 * @template T6
 * @param { [creator<T1>, creator<T2>, creator<T3>, creator<T4>, creator<T5>, creator<T6>] } moduleArr
 * @returns {(set: any)=>{setStore: (param: Partial<T1&T2&T3&T4&T5&T6>)=>void}&T1&T2&T3&T4&T5&T6}
 */
function aggregateStore(moduleArr) {
  return (set, get, api) => {
    return Object.assign(
      { setStore: set },
      ...moduleArr.map((createStoreMod) => createStoreMod(set, get, api))
    );
  };
}

const useAppStore = create(
  devtools(
    storeSetHookMiddire(
      aggregateStore([user, vip, style, app, board, dialog, filter])
    ),
    {
      enabled: !isProd(),
    }
  )
);

export async function refreshVips() {
  const user = useAppStore.getState().user;

  if (isGdMerged(user)) {
    const gdVip = await getGdVipInfo({ embed: "pending_vips" });
    // 用于兼容大企业敏感授权问题
    getCurrentVip();
    const vips = filterMaterailVip(gdVip.vip || []);
    const pendingVips = filterMaterailVip(gdVip.pending_vips || []);
    useAppStore.setState({
      vips: [...vips, ...pendingVips],
    });
    // 获取下载剩余次数，用于轻舟运营弹窗判断
    if (vips.length > 0) {
      const sucaiRight = await getGdUserDownloadRight();

      useAppStore.setState({
        downloadLeft: sucaiRight ? sucaiRight.total - sucaiRight.used : 0,
      });
    }

    return vips;
  } else {
    const { vips: vipResult } = await getCurrentVip();

    useAppStore.setState({
      vips: vipResult,
    });
    // 获取下载剩余次数，用于轻舟运营弹窗判断
    if (vipResult.length > 0) {
      const info = await getUserRightCount([
        { right_code: VIP_RIGHT_IDENTIFICATION },
      ]);

      useAppStore.setState({
        downloadLeft: info?.[0] ? info[0].left : 0,
      });
    }

    return vipResult;
  }
}

export default useAppStore;

const clientInitStore = once((serverSideStore) => {
  useAppStore.setState(serverSideStore);
});

const initStore = (serverSideStore) => {
  if (typeof window === "undefined") {
    // 服务端每次都要初始化所有状态值到初始值，确保前后请求不会互相影响
    const initState = aggregateStore([user, vip, style, app, board])(
      useAppStore.setState,
      useAppStore.getState,
      useAppStore
    );
    useAppStore.setState({ ...initState, ...serverSideStore });
  } else {
    clientInitStore(serverSideStore);
  }
};

export function useInitStore(serverSideStore) {
  initStore(serverSideStore);
  const {
    user,
    setStore,
    refreshDmUnread,
    refreshUnreadMsg,
    refreshLastBoards,
    refreshUserPermissions,
    isSideNavCollapsed,
    sideNavVisible,
    vips,
  } = useAppStore((store) =>
    pick(store, [
      "isSideNavCollapsed",
      "setStore",
      "user",
      "refreshLastBoards",
      "refreshDmUnread",
      "refreshUnreadMsg",
      "refreshUser",
      "refreshUserPermissions",
      "vips",
      "sideNavVisible",
    ])
  );
  const currentOrgId = getOrgIdByUser(user);

  // 更新优惠券数量
  const refreshCouponNum = useMemoizedFn(async () => {
    if (isGdMerged(user)) {
      if (currentOrgId) {
        const res = await getGdCouponNumber();
        useAppStore.setState(() => {
          return {
            couponNum: +res.un_used_count,
          };
        });
      }
    } else {
      const result = await getCounponNum(
        { status: COUPON_STATUS.GOT },
        {
          // 忽略一些错误处理
          ignoreMessage: true,
          ignoreGdPermission: true,
          ignoreError: true,
        }
      );

      if (result.err) return;

      useAppStore.setState({
        couponNum: +result.count,
      });
    }
  });

  // 获取未支付订单信息，用于未支付提醒
  const getOrders = useMemoizedFn(async () => {
    let result;
    if (isGdMerged(user)) {
      if (currentOrgId) {
        result = { list: await getGdVipUnpaid() };
      }
    } else {
      result = await getBizOrderList(
        {
          status: 0,
        },
        {
          ignoreError: true,
          ignoreGdPermission: true,
          ignoreMessage: true,
        }
      );
    }

    if (result?.err) return;

    setStore({
      unPayOrders: result?.list
        ? result.list.filter((order) => {
            // 排除连续包月订单、大企业订单
            return (
              !isBigCompanyOrder(order) &&
              !order.skus.find((sku) => sku?.ref_body?.goods?.is_auto_renew)
            );
          })
        : [],
    });
  });

  const { getTabShareData, getShareUpdateTime } = useTabShare();

  // 所有第一次加载需要初始化请求的数据， 依赖用户登录的，且根据用户id进行变化的
  useMount(async () => {
    if (user) {
      // 从其他tab页共享的数据
      const shareStore = await getTabShareData();

      // 判断是否有 couponNum 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("couponNum")) {
        refreshCouponNum();
      }

      // 判断是否有 dmUnread 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("dmUnread")) {
        refreshDmUnread();
      }

      // 判断是否有 unreadNum 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("unreadNum")) {
        refreshUnreadMsg();
      }

      // 判断是否有 lastBoards 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("lastBoards")) {
        refreshLastBoards();
      }

      // 判断是否有 unPayOrders 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("unPayOrders")) {
        getOrders();
      }

      // 判断是否有 vip 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("vip")) {
        refreshVips();
      }

      // 初始化获取用户信息
      setStore(shareStore, false, true);
    } else {
      // 未登录时，清空数据
      setStore({
        unreadNum: 0,
        dmUnread: 0,
        couponNum: 0,
        lastBoards: [],
        unPayOrders: [],
        expiredVips: null,
        vips: [],
      });
    }
  });

  useUpdateEffect(() => {
    if (user) {
      // 优惠券数量
      refreshCouponNum();
      refreshDmUnread();
      refreshUnreadMsg();
      refreshLastBoards();
      getOrders();
      refreshVips();
    } else {
      // 未登录时，清空数据
      setStore({
        unreadNum: 0,
        dmUnread: 0,
        couponNum: 0,
        lastBoards: [],
        unPayOrders: [],
        expiredVips: null,
        userPermissions: [],
        vips: [],
      });
    }
  }, [user?.user_id, currentOrgId]);

  useEffect(() => {
    if (!user || isGdMerged(user)) {
      refreshUserPermissions();
    }
  }, [refreshUserPermissions, user, vips]);

  const getUser = async () => {
    // 服务端已经获取过用户信息，不需要再次获取
    // const userResult = await refreshUser();

    if (user) {
      loginTracker({
        userId: user.user_id,
        is_cache_login: true,
      });
    }
  };

  const getDomains = async () => {
    const domains = await getGrocery("domain-recommend");
    setStore({
      domains: domains?.list || [],
    });
  };

  const getCategory = async () => {
    const result = await getGrocery("global-categories");
    const list = result?.categroies?.map((category) => ({
      ...category,
      urlname: category.id,
      value: category.id,
    }));

    setStore({
      categories: list || [],
    });
  };

  // 不依赖登录的初始化数据
  useRequest(async () => {
    // 初始化获取用户信息
    getUser();
    /** 杂货铺来源字典数据 */
    getDomains();
    // 分类信息
    getCategory();
  });

  // 定时刷新消息信息
  const { run: startLoop } = useRequest(
    onlyVisibleRun(async () => {
      if (user) {
        refreshDmUnread();
        refreshUnreadMsg();
        refreshCouponNum();
      }
    }),
    {
      refreshDeps: [user],
      // 5分钟刷新
      pollingInterval: 5 * 60 * 1000,
      // 手动开启时间
      manual: true,
    }
  );

  useMount(async () => {
    const updateTime = await getShareUpdateTime();
    // 计算轮询开始时间
    const loopStartTime =
      5 * 60 * 1000 + ((updateTime || Date.now()) - Date.now());

    setTimeout(startLoop, loopStartTime);
  });

  useEffect(() => {
    const scrollbarWidth = getScrollbarWidth();
    document.body.style.setProperty("--scrollbar-width", `${scrollbarWidth}px`);
  }, []);

  // 宽度变化
  useMasonryCompute(isSideNavCollapsed, sideNavVisible, setStore);

  // 页面切换更新最新至最新租户
  useEffect(() => {
    async function pageVisibleOrFocusHandler() {
      if (document.hidden) return;

      const key = `token.org_id.${env}`;

      const org_id = Cookies.get(key);
      const { user, refreshUser } = useAppStore.getState();

      const user_org_id = getOrgIdByUser(user);
      // 未合并账号
      const notMerge = user && !user_org_id;

      // 相等或者两个都为空
      if (org_id === user_org_id || (!org_id && !user_org_id)) {
        return;
      }

      // 如果是刚融合，则更新用户信息, 不刷新页面
      if (!user_org_id) {
        await refreshUser();
        return;
      }

      // 直接刷新页面
      location.reload();

      // 更新当前用户信息
      // await refreshUser();

      // if (notMerge) {
      //   // 从未合并变成合并, 不需要通知租户变更
      //   return;
      // }

      // // 避免更新有差异，然后一直触发更新, 导致接口调用太多
      // // Cookies.set(key, user?.extra?.org_id);
      // // 触发切换组织事件， 用户会员权益相关页面会有权限提示
      // window.dispatchEvent(new Event(EVENT.GD_ORG_SWITCH));
    }

    document.addEventListener("visibilitychange", pageVisibleOrFocusHandler);
    window.addEventListener("focus", pageVisibleOrFocusHandler);

    return () => {
      document.removeEventListener(
        "visibilitychange",
        pageVisibleOrFocusHandler
      );
      window.removeEventListener("focus", pageVisibleOrFocusHandler);
    };
  }, []);

  // 更新组织详细信息
  useEffect(() => {
    // 直接刷新页面
    if (currentOrgId) {
      getMeOrg({ org: 1, org_role: 1 }).then((res) => {
        useAppStore.setState({
          orgInfo: res,
        });
      });
    }
  }, [currentOrgId]);

  // 获取组织列表
  const { data: orgList } = useRequest(
    async () => {
      if (!user || !isGdMerged(user)) {
        return [];
      }
      const res = await getGdUserOrgs();
      return Array.isArray(res) ? res : [];
    },
    {
      refreshDeps: [user],
    }
  );

  // 更新当前组织信息
  useEffect(() => {
    const currentOrg =
      orgList?.find((org) => org.org_id === currentOrgId) ?? null;

    useAppStore.setState({
      orgList,
      currentOrg,
    });
  }, [orgList, currentOrgId]);
}
